import { useRef } from 'react'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'
import { stringParam } from 'lib/query-helper'

import Head from 'next/head'
import { Robots } from 'components/SEO'

import runMiddleware from 'lib/middleware'
import { initializeApollo, addApolloState } from '../../../../../lib/apolloClient'
import { useUserSession, getSessionAccountId } from 'hooks/useUserSession'

import BasePage from 'modules/marketplace/layout/components/BasePage'
import Breadcrumbs from 'modules/marketplace/layout/components/Breadcrumbs'
import { Container } from 'modules/marketplace/layout/components/ResponsiveContainers'
import ProductPictures from '../../components/ProductPictures'
import ProductDetails from '../../components/ProductDetails'
import ProductVarieties from '../../components/ProductVarieties'
import RelatedProductList from '../../components/RelatedProductList'
import RelatedContentSection from '../../components/RelatedContentSection'
import RetailerSignupBanner from 'modules/website/components/RetailerSignupBanner'
import PricingRequestPanel from 'modules/marketplace/pricing/containers/PricingRequestPanel'
import SellerProfile from 'modules/marketplace/common/components/SellerProfile/'
import MarketplaceRecommendationsPanel from 'modules/recommendations/components/MarketplaceRecommendationsPanel'
import InProgressOrderModal from 'modules/marketplace/common/components/InProgressOrderModal'
import InProgressOrderBanner from 'modules/marketplace/common/components/InProgressOrderBanner'
import BannedBuyerAlertAlert from 'modules/buyer-hub/account/components/BannedBuyerAlert'

import GET_RELATED_PRODUCTS_FOR_PUBLISHED_PRODUCT from '../../components/RelatedProductList/graphql/GetRelatedProductsForPublishedProduct.graphql'
import GET_PUBLISHED_PRODUCT from './graphql/GetPublishedProduct.graphql'
import { GetPublishedProduct, GetPublishedProductVariables } from './graphql/__generated__/GetPublishedProduct'
import { BuyerStatusEnum } from '../../../../../../__generated__/globalTypes'

import styles from './ProductPage.module.css'

const ProductPage = () => {
  const router = useRouter()
  const recommendationsRef = useRef(null)

  const queryVariables = {
    sellerSlug: stringParam(router.query.seller) || '',
    catalogSlug: stringParam(router.query.catalog) || '',
    productSlug: stringParam(router.query.product) || '',
    isPreview: stringParam(router.query.preview) === '1'
  }

  const { isGuest } = useUserSession()

  const { data } = useQuery<GetPublishedProduct, GetPublishedProductVariables>(GET_PUBLISHED_PRODUCT, {
    variables: queryVariables,
    skip: !queryVariables.sellerSlug || !queryVariables.catalogSlug || !queryVariables.productSlug
  })

  const seller = data?.marketplaceSeller
  const product = data?.marketplaceSeller?.marketplaceCatalog?.publishedProduct
  const isBuyerBanned = data?.currentAccount?.buyer?.status === BuyerStatusEnum.BANNED || false

  const breadcrumbs = [
    {
      label: seller?.displayName ?? '',
      href: `/${queryVariables.sellerSlug}${queryVariables.isPreview ? '?preview=1' : ''}`
    },
    {
      label: seller?.marketplaceCatalog?.name ?? '',
      href: `/${queryVariables.sellerSlug}/${queryVariables.catalogSlug}${queryVariables.isPreview ? '?preview=1' : ''}`
    },
    { label: product?.name ?? '' }
  ]

  return (
    <BasePage
      hasMarketingFooter={isGuest}
      hasGuestBanner={isGuest}
      isPreview={queryVariables.isPreview}
      hasDepartmentsMenu={true}
      hideIntercom>
      <Head>
        <title>{`Wholesale ${product?.name} - ${seller?.displayName} - Fieldfolio`}</title>
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_MARKETPLACE_URL}${router.asPath}`} />
        <Robots content="follow, index" />
      </Head>

      <Container>
        <BannedBuyerAlertAlert className={styles.banBuyerContainer} />
      </Container>

      {product?.catalog.id && !isBuyerBanned && (
        <Container className={styles.inProgressOrderBannerContainer}>
          <InProgressOrderBanner
            classNameContainer={styles.inProgressOrderBanner}
            catalogId={product?.catalog.id}
            productId={product.id}
          />
        </Container>
      )}

      {seller && product && (
        <>
          {breadcrumbs && <Breadcrumbs crumbs={breadcrumbs} />}
          <Container>
            <div className={styles.coreProduct}>
              <div className={styles.productPictures}>
                <ProductPictures pictures={product.pictures.nodes} />
              </div>

              <div className={styles.productDetailsPanel}>
                <ProductDetails product={product} isPreview={queryVariables.isPreview} />

                {!product.hasPricingAccess && (
                  <div className={styles.pricingRequestsContainer}>
                    <PricingRequestPanel hasNarrowLayout sellerId={seller?.id} catalogIds={[product.catalog.id]} />
                  </div>
                )}

                <ProductVarieties varieties={product.varieties.nodes} badge={product.badge} />
              </div>
            </div>
          </Container>
        </>
      )}

      {seller && (
        <RelatedContentSection isTinted title="About this Seller">
          <SellerProfile isCompact isHero={false} seller={seller} />
        </RelatedContentSection>
      )}

      <RelatedProductList queryVariables={queryVariables} />

      <div ref={recommendationsRef} className={styles.recommendationsPanelContainer}>
        {/* Show only after all of the queries have been fetched */}
        {data?.marketplaceSeller && (
          <Container>
            <MarketplaceRecommendationsPanel className={styles.recommendationsPanel} hideLoading />
          </Container>
        )}
      </div>

      {/* <ExploreRelatedCategories productCategoryTaxonomy={product.productCategoryTaxonomy} /> */}

      {product?.catalog.id && !isBuyerBanned ? <InProgressOrderModal catalogId={product?.catalog.id} /> : null}

      {isGuest && <RetailerSignupBanner />}
    </BasePage>
  )
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  runMiddleware(context)

  const sessionAccountId = getSessionAccountId()

  // Don't bother fetching any data server side if there is an existing session.
  //
  // It feels much faster to just let the client fetch data and render the page
  if (sessionAccountId) {
    return { props: {} }
  }

  const { query } = context
  const apolloClient = initializeApollo()

  await apolloClient.query({
    query: GET_PUBLISHED_PRODUCT,
    variables: {
      sellerSlug: query.seller,
      catalogSlug: query.catalog,
      productSlug: query.product,
      isPreview: query.preview === '1'
    }
  })

  await apolloClient.query({
    query: GET_RELATED_PRODUCTS_FOR_PUBLISHED_PRODUCT,
    variables: {
      sellerSlug: query.seller,
      catalogSlug: query.catalog,
      productSlug: query.product,
      isPreview: query.preview === '1'
    }
  })

  return addApolloState(apolloClient, { props: {} })
}

export default ProductPage
